import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { WIZ_EVENT_TYPE, WIZ_PHYSIC_POSITION } from '@wizeoo/akm-core-lib';
import {
  IWizItemRenderer,
  WizBaseCollector,
  WizCheckbox,
  WizDatasource,
  WizDropdown,
  WizHgroup,
  WizPopUp,
  WizTextarea,
  WizValidationContainerDirective,
  WizValidationErrors,
  WizValidatorFn,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { MeasureVO, WizConstants } from '@wizeoo/akm-compute-lib';
import { AlkEvent } from '../../../../../framework/events/alk.event';
import { FakeVO } from '../../../../../vo/common/tool/fake.vo';
import { FormulaEditorPop } from '../../../../measures/pops/formula-editor.pop';
import { ColorPickerPop } from '../../../../colorpicker/color-picker.pop';
import { ALK_EVENT_TYPE } from '../../../../../model/enums';
import { Model } from '../../../../../model/model';
import { ViewData } from '../../../../../model/view.data';
import { StyleLineVO } from '../../../../../vo/workingdata/styleline.vo';
import { StyleEngine } from '../../../../../manager/workingdata/style.engine';
import { MeasureParams } from '../../../../../manager/workingdata/measure.engine';

/**
 * rendu d'une ligne de style
 */
@Component({
  selector: 'condition-renderer',
  templateUrl: './condition.renderer.html',
})
export class ConditionRenderer implements IWizItemRenderer<null, StyleLineVO>, OnDestroy, AfterViewInit {
  @HostBinding('style.width') forcedWidth = '100%';
  
  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef) {}


  @ViewChild('vgEdition', { read: WizHgroup }) vgEdition: WizHgroup; // ensemble de la zone de détail pour écoute ModelUpdate
  @ViewChild('vgEdition', {
    read: WizValidationContainerDirective,
    static: false,
  })
  private vgEditionCheck: WizValidationContainerDirective; // Zone de validation globale
  @ViewChild('ddlComparator', { read: WizDropdown }) ddlComparator: WizDropdown; // drop du comparateur
  @ViewChild('taFormula', { read: WizTextarea }) taFormula: WizTextarea; // saisie de formule
  @ViewChild('cbBold', { read: WizCheckbox }) cbBold: WizCheckbox; // saisie du bold
  @ViewChild('blockFont') blockFont: ElementRef;
  @ViewChild('blockBack') blockBack: ElementRef;

  public static UNSET_STYLE : string = 'unset';

  // Valeurs des couleurs à conserver
  public fontColor = ConditionRenderer.UNSET_STYLE;
  public backgroundColor = ConditionRenderer.UNSET_STYLE;

  public model: Model;

  public bold: boolean = false;
  public formula: string = '';

  public meas: MeasureVO = null; // construction d'une fausse mesure, pour calcul

  public css: any = null; // la résultante du css, en KeyPairs

  owner: WizBaseCollector;

  public comparatorDS: WizDatasource<FakeVO> = new WizDatasource<FakeVO>(ViewData.getInstance().comparatorList) // fake des comparateurs
    .setSortFields([{ property: 'order', way: 'asc', numeric: true }])
    .setSelectionMode('single')
    .build();

  /** La donnée affichée */
  protected _data: StyleLineVO;
  public get data(): StyleLineVO {
    return this._data;
  }
  public set data(pData: StyleLineVO) {
    this._data = pData;
    this.model = pData.model;
    this.feedContent();
  }

  ngAfterViewInit(): void {
    this.ddlComparator.wizFocus();
    WizEventManager.addListener(this, this.vgEdition, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateModel); // écouteur global change pour modelUpdate
    WizEventManager.addListener(this, this.vgEdition, ALK_EVENT_TYPE.STYLE_ELEMENT_CHANGED, this.updateModel); // écouteur global change pour modelUpdate
  }

  feedContent(): void {
    this.css = this.model.styleEngine.stringToCss(this.data.css);
    this.fontColor = this.css[StyleEngine.STYLE_FONT_COLOR];
    this.backgroundColor = this.css[StyleEngine.STYLE_BACKGROUND_COLOR];
    this.bold = this.css[StyleEngine.STYLE_FONT_WEIGHT] === 'bold';
    this.comparatorDS.select([this.data.comparatorVO]);
    this.formula = this.data.formula;
  }

  /** click sur les icones de suppression de style de couleur */
  public resetColor(who: string): void {
    if (who === 'blockFont') {
      this.fontColor = ConditionRenderer.UNSET_STYLE; 
    } else if (who === 'blockBack') {
      this.backgroundColor = ConditionRenderer.UNSET_STYLE; 
    } 
    this.setStyle();
  }

  // attention, ne PAS surcharger
  ngOnDestroy(): void {
    WizEventManager.removeListener(this, this.vgEdition, WIZ_EVENT_TYPE.MODEL_UPDATE); // écouteur global change pour modelUpdate
  }

  public swapBold(e: Event): void {
    this.bold = !this.bold;
    this.setStyle();
    // e.stopPropagation();
  }

  public focusMeImFamous(e: Event): void {
    e.stopPropagation();
  }

  /**
   * modification d'un élément de modèle
   * @param e WizEvent
   */
  private updateModel(e: WizEvent): void {
    switch (e.component) {
      case this.ddlComparator:
        this.data.comparator = this.comparatorDS.selectedItem ? this.comparatorDS.selectedItem.code : null;
        break;
      case this.taFormula:
        this.data.formula = this.taFormula.wizValue;
        break;
      case this.cbBold:
        this.bold = this.cbBold.wizChecked;
        this.setStyle();
        break;
      default:
        // interception des mauvais update
        return;
    }
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.STYLE_ELEMENT_CHANGED, true));
    // WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }

  public setStyle(): void {
    const parts: string[] = [];
    parts.push(StyleEngine.STYLE_FONT_COLOR + ViewData.KEY_PAIR_SEPARATOR + this.fontColor);
    parts.push(StyleEngine.STYLE_BACKGROUND_COLOR + ViewData.KEY_PAIR_SEPARATOR + this.backgroundColor);

    const weight: string = this.bold ? 'bold' : 'unset';
    parts.push(StyleEngine.STYLE_FONT_WEIGHT + ViewData.KEY_PAIR_SEPARATOR + weight);
    this.data.css = parts.join(ViewData.STYLE_ELEMENT_SEPARATOR);
    this.cdref.markForCheck();
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.STYLE_ELEMENT_CHANGED, true));
    // WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }

  /**
   * affichage de la popUp du colorPicker
   * @param pEvent
   */
  public showColorPicker(pEvent: Event): void {
    pEvent.stopPropagation();
    pEvent.preventDefault();

    if (!this.owner.wizDisabled) {
      if (pEvent.target === this.blockFont.nativeElement) {
        ColorPickerPop.show(this.blockFont.nativeElement, pEvent.target, this.fontColor, this.backFromPicker.bind(this));
      } else if (pEvent.target === this.blockBack.nativeElement) {
        ColorPickerPop.show(this.blockBack.nativeElement, pEvent.target, this.backgroundColor, this.backFromPicker.bind(this));
      }
    }
  }

  public backFromPicker(target: any, color: string): void {
    if (target === this.blockFont.nativeElement) {
      this.fontColor = color;
    } else if (target === this.blockBack.nativeElement) {
      this.backgroundColor = color;
    }
    this.setStyle();
  }

  public doDelete(event: Event): void {
    event.stopImmediatePropagation();
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.STYLE_STEP_DELETE, this.data));
  }

  /**
   * Validateur de la formule
   */
  public formulaValidator(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      // TODO
      // const res: [boolean, string] = MeasureManager.isValidFormula(this.clone, FormulaCoreEngine.MEASURE_MODE_FROM);
      // if (res[0]) {
      //   const temp: WizValidationError = { wizError: '$.measure.error.formulaF.$', wizErrorParams: [res[1]] };
      //   return [temp];
      // }
      return null;
    };
  }

  /**
   * appel de la pop d'édition de formule depuis l'icone Fx
   */
  public showFormulaEditor(): void {
    const callback: Function = this.backFromFormulaPop.bind(this);

    this.meas = this.model.measureEngine.buildFake(); // construction d'une fausse mesure, pour calcul
    this.meas.code = this.data.key;
    this.meas.mode = WizConstants.MEASURE_MODE_INTERACT;
    this.meas.formulaI = this.data.formula;

    let params: MeasureParams = {
      measure: this.meas,
      mode: WizConstants.MEASURE_MODE_INTERACT,
      model: this.model,
      callback
    }


    const editPop: WizPopUp = new WizPopUp(
      FormulaEditorPop,
      true,
      false,
      WIZ_PHYSIC_POSITION.MIDDLE_CENTER,
      '700px',
      '400px',
      params,
      'section-container'
    ).show();

    // const editPop: WizPopUp = new WizPopUp( StyleFormulaEditorPop, true, false, WIZ_PHYSIC_POSITION.MIDDLE_CENTER, '700px', '400px',
    //   [this.data, callback], 'section-container').show();
  }

  /**
   * retour d'édition rapide de formule
   * @param backpop any
   */
  private backFromFormulaPop(backpop: FormulaEditorPop): void {
    if (this.formula !== backpop.currentFormula) {
      this.formula = backpop.currentFormula;
      this.data.formula = this.formula;
      console.log('stlFomr', this.formula);
      this.cdref.markForCheck();
      // WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
      WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.STYLE_ELEMENT_CHANGED, true));
    }
  }

  public mayDelete(): boolean {
    return this.owner.wizDataProvider.length > 1;
  }

  public mayUp(): boolean {
    return this.data.order > 0;
  }

  public mayDown(): boolean {
    return this.data.order < this.owner.wizDataProvider.length - 1;
  }

  public doUp(event: Event): void {
    event.stopImmediatePropagation();
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.STYLE_STEP_UP, this.data));
  }

  public doDown(event: Event): void {
    event.stopImmediatePropagation();

    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.STYLE_STEP_DOWN, this.data));
  }
}
